/**
 * Website-scraping job hooks shared by the two "Add URL" flows:
 * pages/Settings/components/Files/component/NewAddWebsiteModal.jsx (Agent
 * Knowledge + Shared Knowledge tabs) and components/addwebsites/index.js
 * (the Data Interpreter "Add URL" button). Both hit the same routes with the
 * same payload/response shapes:
 *   - POST /assistant/fetchurl        -> kicks off an async scrape job
 *   - GET  /assistant/scrape/status   -> poll for progress
 *   - GET  /assistant/scrape/urls     -> final discovered-URL list
 *   - POST /chat/assistants/website/upload, or
 *     POST /chat/profiles/:id/website/upload for the shared-knowledge variant
 *
 * Both call sites previously polled status with a function that called
 * itself again immediately on every "in_progress" response with NO delay,
 * hammering the endpoint as fast as promises resolved. useScrapeJobStatus
 * replaces that with refetchInterval (2.5s), which stops automatically once
 * the job leaves "in_progress" — or sooner, if the caller reports the job
 * was already submitted via `stopPolling` (mirrors the original
 * `submitUrlsRef` early-stop check).
 *
 * The two call sites still merge the incoming URL lists into their own local
 * state differently (NewAddWebsiteModal appends+dedupes throughout;
 * addwebsites replaces wholesale once the final fetch resolves), so that
 * shaping stays in each component, driven off these hooks' `data`.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST } = ReqMethods;

const POLL_INTERVAL_MS = 2500;

export const scrapingKeys = {
  all: ["scrapeJob"],
  status: (jobId, profileId) => [...scrapingKeys.all, "status", jobId, profileId],
  urls: (jobId, profileId) => [...scrapingKeys.all, "urls", jobId, profileId],
};

export function useStartScrapeJob() {
  return useMutation({
    mutationFn: ({ url, profileId }) =>
      request(POST, "/assistant/fetchurl", { payload: { url, profileId } }),
  });
}

export function useScrapeJobStatus(jobId, profileId, { stopPolling } = {}) {
  return useQuery({
    queryKey: scrapingKeys.status(jobId, profileId),
    queryFn: () =>
      request(GET, "/assistant/scrape/status", {
        query: `jobId=${jobId}&profileId=${profileId}`,
      }),
    enabled: !!jobId && !!profileId,
    refetchInterval: (query) => {
      if (stopPolling?.()) return false;
      return query.state.data?.data?.status === "in_progress" ? POLL_INTERVAL_MS : false;
    },
  });
}

export function useScrapedUrls(jobId, profileId, enabled) {
  return useQuery({
    queryKey: scrapingKeys.urls(jobId, profileId),
    queryFn: () =>
      request(GET, "/assistant/scrape/urls", {
        query: `profileId=${profileId}&jobId=${jobId}`,
      }),
    enabled: !!jobId && !!profileId && !!enabled,
  });
}

// Shared final-submit mutation for both the agent/account variant
// (POST /chat/assistants/website/upload) and the shared-knowledge variant
// (POST /chat/profiles/:id/website/upload) — same shape, different route.
export function useUploadScrapedWebsite() {
  return useMutation({
    mutationFn: ({ isSharedKnowledge, profileId, payload }) =>
      isSharedKnowledge
        ? request(POST, `/chat/profiles/${profileId}/website/upload`, { payload })
        : request(POST, "/chat/assistants/website/upload", { payload }),
  });
}
